"use client"

import { useState } from "react"
import Image from "next/image"
import Link from "next/link"
import { Button } from "@/components/ui/button"
import { ArrowRight, Users, GraduationCap, HeartHandshake } from "lucide-react"
import { MotionDiv, MotionSection } from "@/components/motion"

const programs = [
 {
  title: "Bully Talk Assemblies",
  description:
   "Our interactive school presentations bring the message of kindness and inclusion directly to students. Through storytelling, role-playing, and open dialogue, we create memorable experiences that inspire young people to become upstanders.",
  image: "/images/imgi_75_67a993d6da6122f5bb165fb2_1737767586939-p-500.jpg",
  alt: "Bully Talk school assembly presentation",
  icon: GraduationCap,
  color: "bg-violet",
  rotation: "rotate-1",
 },
 {
  title: "Peer Leaders Program",
  description:
   "Older students mentor younger peers, building a culture of empathy and support from within. Our trained peer leaders model positive behavior, mediate conflicts, and create safe spaces where every student feels they belong.",
  image: "/images/imgi_78_67a993bd8c79bc7275e6e851_1737767583918-p-500.jpg",
  alt: "Bully Talk peer leaders mentoring students",
  icon: Users,
  color: "bg-violet-dark",
  rotation: "-rotate-1",
 },
 {
  title: "Community Workshops",
  description:
   "Engaging workshops for parents, educators, and community members to recognize, prevent, and respond to bullying. We provide practical tools and strategies that can be implemented immediately.",
  image: "/images/imgi_112_66ad943fa2faf76ec2622657_Screen Shot 2024-08-02 at 7.14.54 PM-p-500.png",
  alt: "Bully Talk community workshop",
  icon: HeartHandshake,
  color: "bg-violet",
  rotation: "rotate-1",
 },
]

export default function Programs() {
 const [hoveredIndex, setHoveredIndex] = useState<number | null>(null)

 return (
  <section id="programs" className="py-24 bg-white relative overflow-hidden hidden">
   {/* Decorative blobs */}
   <div className="absolute -top-32 -right-32 w-96 h-96 bg-violet/10 blur-[100px] rounded-full pointer-events-none" />
   <div className="absolute top-1/2 -left-32 w-80 h-80 bg-violet/10 blur-[100px] rounded-full pointer-events-none" />

   <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
    <MotionDiv className="text-center max-w-4xl mx-auto mb-16 relative">
     <div className="inline-flex items-center gap-2 mb-4 bg-violet text-white px-4 py-1.5 transform rotate-2 font-semibold text-sm uppercase tracking-widest shadow-[4px_4px_0px_#000] border-2 border-black">
        TAKE ACTION
     </div>
     <h2 className="text-4xl sm:text-5xl lg:text-7xl text-black uppercase leading-[1] font-antonio font-bold tracking-tight mb-4 relative z-10">
      Empowering Communities<br/>Through{" "}
      <span className="text-violet">PROGRAMS</span>
     </h2>
    </MotionDiv>

    <MotionSection className="grid md:grid-cols-3 gap-6 lg:gap-8 mb-16">
     {programs.map((program, i) => {
      const Icon = program.icon
      return (
       <MotionDiv
        key={program.title}
        variants={{ hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0 } }}
        onMouseEnter={() => setHoveredIndex(i)}
        onMouseLeave={() => setHoveredIndex(null)}
        className={`group relative z-10 hover:z-20 transition-all duration-300 transform ${program.rotation} hover:rotate-0`}
       >
        {/* Layered shadow offsets */}
        <div className={`absolute inset-0 border-[3px] border-black ${program.color} translate-x-3 translate-y-3 -z-10 transition-transform duration-300 group-hover:translate-x-4 group-hover:translate-y-4`} />
        <div className="absolute inset-0 bg-black translate-x-1.5 translate-y-1.5 -z-10" />

        <div className="bg-white border-[3px] border-black overflow-hidden flex flex-col h-full">
         <div className="relative aspect-square overflow-hidden border-b-[3px] border-black p-2 bg-slate-50">
          <div className="relative w-full h-full border-2 border-black overflow-hidden bg-white">
             <Image
              src={program.image}
              alt={program.alt}
              fill
              className="object-cover object-top transition-transform duration-700 group-hover:scale-105"
             />
             {/* Torn tape detail */}
             <div className="absolute top-2 left-1/2 w-16 h-6 -ml-8 bg-white/60 backdrop-blur-sm transform -rotate-2 border border-white opacity-80" />
          </div>
         </div>
         <div className="p-4 lg:p-5 flex flex-col flex-grow relative bg-white">
          <div className={`absolute top-0 right-0 w-10 h-10 flex items-center justify-center border-l-[3px] border-b-[3px] border-black bg-white`}>
             <Icon strokeWidth={2.5} size={16} className="text-violet" />
          </div>
          <h3 className="text-lg lg:text-xl text-black mb-3 font-antonio font-bold uppercase pr-10 leading-tight">
           {program.title}
          </h3>
          <p className="text-black leading-relaxed mb-4 text-sm font-normal flex-grow">
           {program.description}
          </p>
          <a
           href="/programs/bully-talk-assembly"
           className={`inline-flex items-center text-black font-antonio font-bold uppercase tracking-widest text-sm hover:text-violet transition-colors group/link mt-auto inline-block py-2 w-max border-b-4 border-transparent hover:${program.color.replace('bg-', 'border-')}`}
          >
           Explore Program
           <ArrowRight className="w-5 h-5 ml-2 transition-transform group-hover/link:translate-x-2" />
          </a>
         </div>
        </div>
       </MotionDiv>
      )
     })}
    </MotionSection>

    <MotionDiv className="text-center relative z-20">
     <Link href="/programs">
       <Button className="mx-auto bg-black text-white px-10 py-6 text-lg font-antonio font-bold rounded-none uppercase tracking-widest shadow-[6px_6px_0px_0px_#5A3290] transition-all hover:bg-[#5A3290] hover:-translate-y-1 flex items-center gap-2">
        View All Programs
       </Button>
     </Link>
    </MotionDiv>
   </div>
  </section>
 )
}